/* System defined core library */
import { Injectable } from '@angular/core';
import { Observable, Observer, Subscription } from 'rxjs';

/* User defined library/components */
import { OrchestrationApiService } from '../../scheduling/shared/services/orchestration-api.service';
import { SelectedExamDetailService } from '../../scheduling/shared/services/selected-exam-detail.service';

/* User defined models */
import { ProSchedulerCheckbox } from '../models/common/checkbox.model';
import { ProSchedulerDatePicker } from '../models/common/datepicker.model';
import { ProSchedulerDropdown } from '../models/common/dropdown.model';
import { DynamicFormBase } from '../models/common/dynamic-form-base.model';
import { ProSchedulerLinkedCombo } from '../models/common/linkedCombo.model';
import { Dictionary } from '../models/common/shared.model';
import { ProSchedulerTextbox } from '../models/common/textbox.model';
import { Candidate } from '../models/scheduling/candidate.model';
import { Parameter, TestingAccommodation } from '../models/scheduling/testingAccommodation.model';


@Injectable()
export class DynamicComponentService {

  public apiresponse: any[] = [];
  public errorMessage: string = '';
  public candidate: Candidate;

  candidate$: Subscription;

  constructor(
    private orchestrationApiService: OrchestrationApiService,
    private selectedExamDetailService: SelectedExamDetailService
  ) {

    this.candidate$ = this.selectedExamDetailService.candidate.subscribe(
      candidate => {
        this.candidate = candidate;
      });
  }

  getEligibilityCriteriaMetaData(clientCode: string, programCode: string): Observable<any[]> {
    let dynamicFormBase: DynamicFormBase<any>[] = [];
    return new Observable((observer: Observer<any>) => {
      this.orchestrationApiService.getFlows(clientCode, programCode).subscribe((response: any) => {
        response = response.filter(function (el: any) {
          return el.name == "Validate Eligibility";
        });
        this.apiresponse = response[0].data;
        this.apiresponse.forEach((element: any, index: any) => {
          // need to remove the below code once we are getting respective data from API.
          let order: number = index + 1;
          let validationMessage: string = element.name;
          // TODO: Remove this when we are sure we won't rollback
          let maxLength: number = 255;
          if (!!element.parameters.length) {
            maxLength = element.parameters.length;
          } else if (!!element.parameters.maxlength) {
            maxLength = element.parameters.maxlength
          }
          let pattern: string = ".*";
          // TODO: Remove this when we are sure we won't rollback
          // We need to use {name}_{display} for translations and label for fallback if using the new formatting and {display(no spaces)} for both if using the old formatting
          let altLabel: string = element.parameters.display.replace(/ /g, '');
          let label: string = element.parameters.display.replace(/ /g, '');
          if (!!element.parameters.fieldType) {
            altLabel = element.name + '_' + element.parameters.display.replace(/ /g, '_');
            label = element.parameters.display;
          }


          dynamicFormBase.push(new ProSchedulerTextbox({
            key: element.name,
            label: label,
            required: element.parameters.required,
            order: order,
            validationMessage: validationMessage,
            pattern: pattern,
            maxlength: maxLength,
            alternativeLabel: altLabel
          }));
        });

        dynamicFormBase = dynamicFormBase.sort((a, b) => a.order - b.order)
        if (dynamicFormBase.length == this.apiresponse.length) {
          observer.next(dynamicFormBase);
          observer.complete();
        }
      });

    });
  }

  getCandiateProfileMetaData(clientCode: string, programCode: string, examCode: string): Observable<DynamicFormBase<any>[]> {
    return new Observable((observer: Observer<DynamicFormBase<any>[]>) => {
      this.orchestrationApiService.getFlows(clientCode, programCode, examCode).subscribe((response: any) => {
        observer.next(this.formatCandiateProfileMetaData(response));
        observer.complete();
      });
    });
  }

  formatCandiateProfileMetaData(orchestrationData: any): DynamicFormBase<any>[] {
    if (orchestrationData.length > 0) {
      const dynamicFormBase: DynamicFormBase<any>[] = [];
      if (orchestrationData.length > 0) {
        orchestrationData = orchestrationData.filter(function (el: any) {
          return el.name === 'Candidate Profile';
        });
        this.apiresponse = orchestrationData[0].data;
        let parent: ProSchedulerDropdown;
        const children: DynamicFormBase<any>[] = [];

        this.apiresponse.forEach((element: any) => {
          let controlmetata;
          let validationMessage: string = '';
          const required: boolean = element.parameters.required === 'True' ? true : false;
          const readOnly: boolean = element.parameters.readOnly === 'True' ? true : false;
          const displayFlag: boolean = element.parameters.displayFlag === 'True' ? true : false;
          const altLabel: string = !!element.parameters.validationData && element.parameters.validationData.includes('Memo=') ? element.parameters.validationData.split('Memo=')[1].replace(/ /g, '_') : '';

          validationMessage = element.name;
          if (element.name.toLowerCase() === 'birth_date') {
            element.parameters.fieldType = 'datepicker';
          }
          switch (element.parameters.fieldType) {
            case 'text':
              controlmetata = new ProSchedulerTextbox({
                key: element.name,
                label: element.parameters.displayName,
                alternativeLabel: altLabel,
                required: required,
                value: element.parameters.initialValue,
                validationMessage: validationMessage,
                validationData: element.name === 'SSN' ? '' : element.parameters.validationData, // conditional check for SSN key removing validation
                length: element.parameters.length,
                readOnly: readOnly,
                fieldMap: element.parameters.fieldMap,
                displayFlag: displayFlag
              });
              break;
            case 'select':
              controlmetata = new ProSchedulerDropdown({
                key: element.name,
                label: element.parameters.displayName,
                alternativeLabel: altLabel,
                required: required,
                options: [],
                validationMessage: validationMessage,
                validationData: element.parameters.validationData,
                values: element.parameters.values ? JSON.parse(element.parameters.values) : [],
                readOnly: readOnly,
                value: element.parameters.initialValue,
                fieldMap: element.parameters.fieldMap,
                displayFlag: displayFlag
              });
              break;
            case 'linkedselect':
              controlmetata = new ProSchedulerLinkedCombo({
                key: element.name,
                label: element.parameters.displayName,
                parentLabel: element.parameters.parentDisplayName,
                alternativeLabel: altLabel,
                required: required,
                options: [],
                validationMessage: validationMessage,
                validationData: element.parameters.validationData,
                values: element.parameters.linkedValues,
                readOnly: readOnly,
                value: element.parameters.initialValue,
                fieldMap: element.parameters.fieldMap,
                displayFlag: displayFlag
              });
              break;
            case 'checkbox':
              controlmetata = new ProSchedulerCheckbox({
                key: element.name,
                label: element.parameters.displayName,
                alternativeLabel: altLabel,
                required: required,
                value: element.parameters.initialValue,
                validationMessage: validationMessage,
                validationData: element.parameters.validationData,
                fieldMap: element.parameters.fieldMap,
                displayFlag: displayFlag
              });
              break;
            case 'datepicker':
              controlmetata = new ProSchedulerDatePicker({
                key: element.name,
                label: element.parameters.displayName,
                alternativeLabel: altLabel,
                required: required,
                value: element.parameters.initialValue,
                validationMessage: validationMessage,
                validationData: element.parameters.validationData,
                length: element.parameters.length,
                readOnly: readOnly,
                fieldMap: element.parameters.fieldMap,
                displayFlag: displayFlag
              });
              break;
          }
          if (controlmetata.key === 'COUNTRY') {
            controlmetata['options'] = controlmetata.values;
            parent = <ProSchedulerDropdown>controlmetata;
          } else if (controlmetata.key === 'ZIPCODE' || controlmetata.key === 'COUNTY_NAME' || controlmetata.key === 'STATE') {
            children.push(controlmetata);
          }
          dynamicFormBase.push(controlmetata);
        });

        if (!!parent && !!children.length) {
          parent.children = children;
          children.forEach(c => c.setParent(parent));
        }

        if (this.apiresponse.length >= dynamicFormBase.length) {
          const emailIndex: number = dynamicFormBase.findIndex(candidate => candidate.key.toLowerCase().trim() === 'email');
          if (emailIndex > -1) {
            let validateEmailBase: any;
            if (dynamicFormBase[emailIndex] != null && dynamicFormBase[emailIndex] != undefined) {
              validateEmailBase = new ProSchedulerTextbox({
                key: 'VALIDATE_EMAIL',
                label: 'Validate Email',
                required: dynamicFormBase[emailIndex].required,
                value: '',
                validationMessage: dynamicFormBase[emailIndex].validationMessage,
                validationData: dynamicFormBase[emailIndex].validationData,
                length: dynamicFormBase[emailIndex].length,
                readOnly: dynamicFormBase[emailIndex].readOnly,
                displayFlag: dynamicFormBase[emailIndex].displayFlag
              });
              dynamicFormBase.splice(emailIndex + 1, 0, validateEmailBase);
            }
          }
          this.selectedExamDetailService.setDynamicFormBase(dynamicFormBase);
          return dynamicFormBase;
        }
      }
    }
  }

  setCandidateProfileforSkip(response: any): void {
    let dynamicInputCtrls = this.formatCandiateProfileMetaData(response);
    if (!!this.candidate.defaultDetails) {
      this.candidate.details = new Dictionary();

      dynamicInputCtrls.forEach((ctrlObj) => {
        this.candidate.details
          .add(ctrlObj.key, this.candidate.defaultDetails[ctrlObj.key.toString().toLowerCase()]);
        if (ctrlObj.key.toString().toLowerCase() === 'firstname_rsa') {
          this.candidate.details.add(ctrlObj.key, this.candidate.defaultDetails['first_name']);
        }
        if (ctrlObj.key.toString().toLowerCase() === 'lastname_rsa') {
          this.candidate.details.add(ctrlObj.key, this.candidate.defaultDetails['last_name']);
        }
        if (!this.candidate.details.containsKey(ctrlObj.key)) {
          this.candidate.details.add(ctrlObj.key, this.candidate.defaultDetails[ctrlObj.fieldMap.toString().toLowerCase()]);
        }
        this.selectedExamDetailService.SetCandidateDataSource(this.candidate);
        this.selectedExamDetailService.shouldUpdateDemographics = false;
      })
    }
  }

  getTestingAccomodationsMetaData(clientCode: string, programCode: string, examCode: string): Observable<TestingAccommodation[]> {
    let testingAccommodations: TestingAccommodation[] = [];
    let extraData: TestingAccommodation[] = [];

    return new Observable((observer: Observer<TestingAccommodation[]>) => {

      this.orchestrationApiService.getFlows(clientCode, programCode, examCode).subscribe((response: any) => {
        if (response.length > 0) {
          this.apiresponse = response.filter(function (el: any) {
            return el.name === "Candidate Selectable";
          });

          if (this.apiresponse.length) {
            if (this.apiresponse[0].extraData) {
              extraData = this.apiresponse[0].extraData;
            }

            this.apiresponse[0].data.forEach((element: any) => {
              let tmpTestingAccommodation: TestingAccommodation;
              let tmpParameter: Parameter;
              let splitOptions: any[] = [];

              if (element.parameters) {

                if (element.parameters["display"] != undefined && element.parameters["display"] != null) {
                  tmpParameter = new Parameter(
                    "display",
                    element.parameters["display"]
                  );
                }

                if (element.parameters["controlType"] != undefined && element.parameters["controlType"] != null) {
                  tmpParameter = new Parameter(
                    "controlType",
                    element.parameters["controlType"]
                  );
                }

                if (element.parameters["dependency"] != undefined && element.parameters["dependency"] != null) {
                  tmpParameter = new Parameter(
                    "dependency",
                    element.parameters["dependency"]
                  );
                  tmpTestingAccommodation.parameters.push(tmpParameter);
                }

                if (element.parameters["option"] != undefined && element.parameters["option"] != null) {
                  if (String(element.parameters["option"]).indexOf(",") > -1) {
                    splitOptions = String(element.parameters["option"]).split(",");
                    tmpParameter = new Parameter(
                      "option",
                      element.parameters["option"],
                      (splitOptions.length) ? splitOptions[0] : ""
                    );
                  }
                }
              }

              tmpTestingAccommodation = new TestingAccommodation(
                element.name,
                null,
                element.parameters["displayName"],
                element.parameters["controlType"],
                element.parameters["dependency"],
                splitOptions, [tmpParameter], null,
                false, null, element.parameters["display"].toString().replace(/ /g, "")
              )


              if (extraData.length) {
                extraData.forEach((tmpExtraData: any) => {
                  if (tmpExtraData.name == tmpTestingAccommodation.name) {
                    tmpTestingAccommodation.id = tmpExtraData.id;
                  }
                });
              }
              testingAccommodations.push(tmpTestingAccommodation);
              if (this.apiresponse[0].data.length == testingAccommodations.length) {
                observer.next(testingAccommodations);
                observer.complete();
              }
            });
          }
        }
      });
    });
  }

  getNotificationData(clientCode: string, programCode: string): Observable<any[]> {
    let dynamicFormBase: DynamicFormBase<any>[] = [];
    return new Observable((observer: Observer<any>) => {
      this.orchestrationApiService.getFlows(clientCode, programCode).subscribe((response: any) => {
        response = response.filter(function (el: any) {
          return el.name == "Verification";
        });
        this.apiresponse = response[0].data;

        observer.next(this.apiresponse);
        observer.complete();
      });

    });
  }
}
